/**
 * Call variable resolution
 *
 * Call variables (CPR, language, ...) reach the client as transient CALL_VARIABLE realtime events.
 * The PBX does not persist them and never replays them, so a client that was not connected at the
 * moment the IVR set the value never learns it. This resolver fills that gap by asking the API for
 * a variable the client does not have, while making sure we ask at most once per call and variable.
 */

/** Key a variable is cached under - the channel it belongs to, not the call leg that displays it. */
export function callVariableKey(channel: string, variable: string) {
	return `${channel}-${variable}`
}

/**
 * Pull the channel id out of a call id
 * @param callId Call id on the form <pbx-hostname>:<channel-id>
 * @returns The channel id, or undefined if the id has no channel part
 */
export function channelFromCallId(callId: string) {
	return callId.split(':')[1] || undefined
}

/**
 * Id of the call leg that carries the variables.
 *
 * Variables are set on the caller's channel while the caller is in the IVR. On an inbound call the
 * agent sees the PBX -> agent leg, which has its own channel and no variables of its own, so we
 * have to address the caller's leg explicitly.
 * @param call Call to address
 * @returns Call id to ask the PBX about
 */
export function callerCallId(call: { _id: string, pbx?: string, callerChannelId?: string }) {
	if (call.callerChannelId && call.pbx) { return `${call.pbx}:${call.callerChannelId}` }
	return call._id
}

export interface CallVariableResolverOptions {
	/** Cache of resolved variables, shared with the employees store */
	cache: Map<string, string>
	/** Asks the server for one variable - resolves to the value, or null when the call has no such variable */
	fetchVariable: (callId: string, variable: string) => Promise<string | null>
}

/**
 * Create a resolver that keeps the call variable cache filled
 * @param options Cache to fill and the function used to ask the server
 * @returns Resolver
 */
export function createCallVariableResolver({ cache, fetchVariable }: CallVariableResolverOptions) {
	/** Lookups that came back empty, so we do not ask again until the next reconnect */
	const missing = new Set<string>()
	/** Lookups in flight, so several panels showing the same call only cause one request */
	const inFlight = new Map<string, Promise<void>>()

	/**
	 * Get a call variable, fetching it in the background if we do not have it yet
	 * @param callId Call id on the form <pbx-hostname>:<channel-id>
	 * @param variable Name of the variable
	 * @returns The value if we already have it, otherwise undefined
	 */
	function resolve(callId: string | undefined, variable: string | undefined) {
		if (!callId || !variable) { return undefined }

		const channel = channelFromCallId(callId)
		if (!channel) { return undefined }

		const key = callVariableKey(channel, variable)
		const cached = cache.get(key)

		if (cached !== undefined) { return cached }
		if (missing.has(key) || inFlight.has(key)) { return undefined }

		const request = fetchVariable(callId, variable)
			.then(value => {
				if (value) { cache.set(key, value) }
				else { missing.add(key) }
			})
			.catch(() => { missing.add(key) })
			.finally(() => { inFlight.delete(key) })

		inFlight.set(key, request)
		return undefined
	}

	/**
	 * Get a call variable, waiting for the server if we do not have it yet.
	 *
	 * The dashboard can show a blank field and fill it in when the value arrives, but an integration
	 * cannot - SendKeys types once and is gone. Those callers must wait for the answer.
	 * @param callId Call id on the form <pbx-hostname>:<channel-id>
	 * @param variable Name of the variable
	 * @returns The value, or undefined if the call has no such variable
	 */
	async function resolveAsync(callId: string | undefined, variable: string | undefined) {
		const cached = resolve(callId, variable)

		if (cached !== undefined || !callId || !variable) { return cached }

		const channel = channelFromCallId(callId)
		if (!channel) { return undefined }

		const key = callVariableKey(channel, variable)

		await inFlight.get(key) // resolve() started one unless we already know it is missing

		return cache.get(key)
	}

	/** Forget which lookups came back empty, so they are retried - used after a realtime reconnect */
	function invalidate() {
		missing.clear()
	}

	/**
	 * Drop what we remember about a channel that has ended
	 * @param channel Channel id
	 */
	function forgetChannel(channel: string) {
		for (const key of missing) {
			if (key.startsWith(`${channel}-`)) { missing.delete(key) }
		}
	}

	/** Wait for every in-flight lookup to finish - for tests */
	async function settled() {
		await Promise.allSettled([ ...inFlight.values() ])
	}

	/** Number of lookups remembered as empty - for tests */
	function missingCount() {
		return missing.size
	}

	return { resolve, resolveAsync, invalidate, forgetChannel, settled, missingCount }
}
